import * as React from "react";
import { useParams } from "react-router-dom";
import { getPresentationBySlug } from "@/data/mockPresentations";
import { getSellerAnalysisBySlug } from "@/data/sellerAnalysisData";
import { PresentationPage } from "@/components/presentation/PresentationPage";
import { fetchPublicPresentation, trackPresentationEvent } from "@/lib/api";
import { getStoredAccessToken, publicPresentationPdfUrl } from "@/lib/adminApi";
import { isPdfRenderMode } from "@/lib/pdfMode";
import { NotFound } from "./NotFound";

function usePresentationMeta(data?: {
  noindex?: boolean;
  owner?: { firstName?: string };
  property?: { address?: string; unit?: string; building?: string };
}) {
  React.useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    const addr = data.property?.address || "this home";
    const unit = data.property?.unit ? ` ${data.property.unit}` : "";
    const building = data.property?.building ? `${data.property.building} · ` : "";
    document.title = `${building}${addr}${unit}`.trim();

    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    if (data.noindex !== false) document.head.appendChild(meta);

    return () => {
      document.title = prevTitle;
      if (data.noindex !== false && meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, [data]);
}

export default function PublicPresentation() {
  const { slug } = useParams<{ slug: string }>();
  const mock =
    getSellerAnalysisBySlug(slug) || getPresentationBySlug(slug);
  const [data, setData] = React.useState<any | undefined>(mock);
  const [loading, setLoading] = React.useState(!mock);
  const [notFound, setNotFound] = React.useState(false);
  const [showPreviewBadge, setShowPreviewBadge] = React.useState(false);
  const pdfMode = isPdfRenderMode();

  React.useEffect(() => {
    if (pdfMode) {
      document.documentElement.setAttribute("data-pdf", "1");
      return () => document.documentElement.removeAttribute("data-pdf");
    }
  }, [pdfMode]);

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!slug) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      const remote = await fetchPublicPresentation(slug);
      if (cancelled) return;
      if (remote?.presentation_data) {
        setData(remote.presentation_data);
        setNotFound(false);
        // Never count Chromium PDF renders as seller opens.
        if (!isPdfRenderMode()) {
          trackPresentationEvent(slug, "page_view");
        }
      } else if (mock) {
        setData(mock);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [slug, mock]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const wantsPreview = params.get("preview") === "1";
      const isAdmin = Boolean(getStoredAccessToken());
      if (!cancelled) setShowPreviewBadge(isAdmin && wantsPreview && !pdfMode);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, pdfMode]);

  usePresentationMeta(data);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory">
        <p className="font-serif text-xl text-charcoal-muted">Loading…</p>
      </div>
    );
  }

  if (notFound || !data) {
    return <NotFound />;
  }

  const pdfHref = slug ? publicPresentationPdfUrl(slug) : null;

  return (
    <>
      {showPreviewBadge && (
        <div
          className="fixed right-4 top-4 z-[60] rounded-full bg-charcoal/90 px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.16em] text-ivory"
          data-pdf-hide
        >
          Admin preview
        </div>
      )}
      {!pdfMode && pdfHref && (
        <div
          className="fixed bottom-5 right-5 z-[55] md:bottom-8 md:right-8"
          data-pdf-hide
          data-testid="pdf-download-bar"
        >
          <a
            href={pdfHref}
            className="inline-flex items-center border border-charcoal/15 bg-ivory/95 px-4 py-2.5 text-[0.65rem] font-medium uppercase tracking-[0.16em] text-charcoal shadow-sm backdrop-blur-sm transition-colors hover:border-charcoal/40 hover:bg-white"
            data-testid="download-pdf"
          >
            Download PDF
          </a>
        </div>
      )}
      <PresentationPage data={data} />
    </>
  );
}
